import { Injectable, NgZone, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NGXLogger } from 'ngx-logger';
import { NotificationService } from './notification.service';
import { SideBarMenuService } from '../services/side-bar-menu.service';
import { ForcedLogoutDialogService } from '../services/forced-logout-dialog.service';
import { AuthService } from '../services/tally-sheet/auth.service';
import { DashboardSocketService } from '../modules/dashboard-v6/services/dashboard-socket.service';
import { SubscriptionActivationService } from '../services/subscription-activation.service';
import { ScreenSupportService } from '../modules/screen-support/screen-support.service';

/**
 * Real-time notifications (bell, role change, forced logout).
 *
 * 2026-09-28 (LIVE_UPDATES_NO_POLLING_PLAN.md step 2): no longer opens its own connection. The app has ONE
 * live connection (DashboardSocketService) that also carries /user/queue/notifications; this service starts
 * it on login, listens to its notifications$, and reconciles the unread count + list after every (re)connect
 * and when the app / tab comes back. The 30 s REST poll that used to run while this socket was down is gone
 * — a reconnect (event-driven, capped at 25 s apart) reconciles instead.
 *
 * Auth rides on the same HttpOnly session cookie every other API call already uses; native (Capacitor)
 * connects same-origin through app.lumberlinq.com/ws-native (see DashboardSocketService).
 */
@Injectable({ providedIn: 'root' })
export class NotificationSocketService {
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly live = inject(DashboardSocketService);
  private readonly subscriptionActivation = inject(SubscriptionActivationService);
  private readonly screenSupport = inject(ScreenSupportService);

  private started = false;
  private subs: Subscription[] = [];
  // Page-level listeners are attached exactly once per page load, even across logout -> login.
  private listenersRegistered = false;

  constructor(
    private notificationService: NotificationService,
    private sideBarMenuService: SideBarMenuService,
    private forcedLogoutDialogService: ForcedLogoutDialogService,
    private authService: AuthService,
  ) {}

  /** Idempotent — safe to call on every route transition (app.component.ts). */
  init(): void {
    if (this.started) return;
    this.started = true;
    this.subs.push(this.live.notifications$.subscribe((body) => this.handleMessage(body)));
    // Reconcile anything sent while we were not connected — a reconnect does not replay what was missed.
    this.subs.push(this.live.connected$.subscribe(() => this.reconcile()));
    this.live.start();
    this.reconcile(); // covers the handshake window before the first connect
    // Customer screen support: look once for a session waiting for this person, and again whenever the server says one changed.
    // Here (after login) and not in the prompt component, which also exists on the login and public pages.
    this.screenSupport.status().subscribe({ error: () => {} });
    this.subs.push(this.live.watch(['SCREEN_SUPPORT'], () => this.screenSupport.status().subscribe({ error: () => {} }), 400));
    this.registerLifecycleListeners();
  }

  /** Logout: close the shared connection (no retries against a dead session) and reset for next login. */
  shutdown(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
    this.screenSupport.myStatus$.next(null);   // never show this person's pending support invitation to the next login on this browser
    this.live.shutdown();
    this.started = false;
  }

  private reconcile(): void {
    this.notificationService.refreshUnreadCount();
    this.notificationService.notifyRefresh();
  }

  private handleMessage(body: string): void {
    // Deliberately not parsing/merging the message body into local state — just a trigger to refetch
    // the authoritative count/list. Cheap (one small GET), avoids any drift between what the
    // socket delivered and what the server actually has, and reuses the exact same refresh path
    // accept/decline/markRead already use (NotificationService.refreshSource).
    this.logger.debug('[NotificationSocket] message received', body);
    this.notificationService.refreshUnreadCount();
    this.notificationService.notifyRefresh();

    // Real gap found 2026-07-27: an admin changing this user's role/permissions had no way to
    // reach their already-open session — the sidebar menu and cached permission snapshot stayed
    // exactly as they were until a manual reload. ROLE_CHANGED is the one live signal this client
    // gets for "your access changed," so use it to trigger an immediate refresh. This is the one
    // field read from the payload (not "merging state" — just deciding whether a second refresh
    // is warranted); requestMenuRefresh() already does both halves (SideBarComponent's own
    // refreshMenu$ subscriber calls loadMenuItems(true) AND permissionService.refreshPermissions()).
    try {
      const parsed = JSON.parse(body);
      if (parsed?.type === 'ROLE_CHANGED') {
        this.sideBarMenuService.requestMenuRefresh();
      }
      // A payment for this company succeeded (plan bought / renewed / upgraded): rights and menu may have changed for every
      // user of the company, so an already-open session refreshes them without a reload. PAYMENT_SUCCEEDED reaches the owner
      // and admins; SUBSCRIPTION_ACTIVATED reaches every other user of the company.
      if (parsed?.type === 'PAYMENT_SUCCEEDED' || parsed?.type === 'SUBSCRIPTION_ACTIVATED') {
        this.sideBarMenuService.requestMenuRefresh();
      }
      // A bank transfer / cheque of this company was approved: the whole "plan became active" refresh (menu, rights, Inventory /
      // Process visibility), the same routine the card-success page uses, so an open tab anywhere in the app is current at once.
      if (parsed?.type === 'OFFLINE_PAYMENT_APPROVED') {
        this.subscriptionActivation.refreshAccess().subscribe();
      }
      // Support asked to see this person's screen (or is ready after their request): the prompt window opens at once.
      if (parsed?.type === 'SCREEN_SUPPORT_REQUESTED') {
        this.screenSupport.status().subscribe({ error: () => {} });
      }

      // Gap found 2026-09-04: this exact message was already arriving here the whole time — the
      // backend has published SESSION_FORCED_LOGOUT over this same socket since the feature
      // shipped — but nothing on this side ever acted on it. The old tab just sat there until
      // its own next API call happened to 401 and the reactive SESSION_INVALIDATED_ELSEWHERE path
      // (tallysheet-http.interceptor.ts) caught it. This makes it instant instead, the same way
      // ROLE_CHANGED already live-updates an open tab above — without waiting for a click.
      //
      // This event is delivered to EVERY currently-connected socket for the user, including the
      // NEW device that just logged in (the one that did the kicking) — payload.deviceId is the
      // ONLY thing that tells them apart, compared against this tab's own device fingerprint
      // (the same value AuthService already sends as the 'deviceId' header on
      // logoutAllOtherDevices()). Only open the dialog when it matches; otherwise this socket
      // belongs to some other device of the same user and must not react.
      if (parsed?.type === 'SESSION_FORCED_LOGOUT') {
        const myDeviceId = this.authService.fingerprint?.toString();
        const kickedDeviceId = parsed?.payload?.deviceId;
        if (myDeviceId && kickedDeviceId && myDeviceId === kickedDeviceId) {
          const timestampUtc = parsed?.payload?.timestampUtc;
          this.forcedLogoutDialogService.open({
            device: parsed?.payload?.device || null,
            location: parsed?.payload?.location || null,
            kickedAtEpochMillis: timestampUtc ? new Date(timestampUtc).getTime() : null,
          });
        }
      }
    } catch {
      // Malformed/unexpected body — the count/list refresh above already ran regardless.
    }
  }

  private registerLifecycleListeners(): void {
    if (this.listenersRegistered) return;
    this.listenersRegistered = true;
    // The shared connection reconnects itself on resume / visible / online; here we only re-read the
    // unread count once when the user comes back (an event, not a timer).
    if (Capacitor.isNativePlatform()) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive || !this.started) return;
        this.ngZone.run(() => this.reconcile());
      });
    } else {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || !this.started) return;
        this.ngZone.run(() => this.reconcile());
      });
    }
  }
}
